import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { PaymentStatusBadge } from "@/components/admin/StatusBadge";
import { BookingWithClient, listBookings } from "@/lib/bookingsApi";
import {
  CATEGORY_LABELS,
  formatDisplayDate,
  formatTime,
} from "@/lib/bookingLabels";
import { formatGbpFromPence } from "@/types/database";
import { isSupabaseConfigured } from "@/lib/supabase";

const BookingsList = () => {
  const [params] = useSearchParams();
  const [rows, setRows] = useState<BookingWithClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [q, setQ] = useState(() => params.get("q") ?? "");

  useEffect(() => {
    document.title = "All Bookings · Olivia Flemming Admin";
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const res = await listBookings({ includeCancelled: true });
      if (!alive) return;
      if (res.ok === false) {
        setError(res.error);
        setRows([]);
      } else {
        setError(null);
        setRows(res.data);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((b) => {
      if (!showCancelled && b.booking_status === "cancelled") return false;
      if (b.booking_status === "archived" && !showCancelled) return false;
      if (!needle) return true;
      const hay = [
        b.reference,
        b.clients?.venue_or_client_name,
        b.clients?.email,
        b.address,
        b.venue_postcode,
        CATEGORY_LABELS[b.category],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, showCancelled, q]);

  return (
    <AdminShell
      title="All Bookings"
      actions={
        <Link
          to="/admin/bookings/new"
          className="inline-flex items-center gap-2 rounded-full bg-primary text-black px-6 py-3 text-sm font-medium hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          New Booking
        </Link>
      }
    >
      {!isSupabaseConfigured && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100/90">
          Connect Supabase env vars to load live bookings.
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search venue, ref, email…"
          className="flex-1 rounded-xl bg-secondary border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-primary"
        />
        <label className="inline-flex items-center gap-2 text-sm text-white/60 font-inter px-1">
          <input
            type="checkbox"
            checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="rounded border-white/20"
          />
          Show cancelled / archived
        </label>
      </div>

      {loading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/50 text-sm">No bookings match.</p>
      ) : (
        <>
        <ul className="md:hidden space-y-3">
          {filtered.map((b) => (
            <li key={b.id}>
              <Link
                to={`/admin/bookings/${b.id}`}
                className="block rounded-2xl border border-white/10 bg-white/[0.03] p-4 active:bg-white/[0.08] font-inter"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-base text-white truncate">{b.clients?.venue_or_client_name ?? "Unnamed"}</p>
                    <p className="text-xs text-white/45 mt-0.5">{CATEGORY_LABELS[b.category]} · {b.reference}</p>
                  </div>
                  <p className="text-base text-primary shrink-0">{formatGbpFromPence(b.fee_pence)}</p>
                </div>
                <div className="flex items-center justify-between gap-3 mt-3">
                  <p className="text-sm text-white/70">{formatDisplayDate(b.performance_date)} · {formatTime(b.start_time)}</p>
                  <PaymentStatusBadge paymentStatus={b.payment_status} bookingStatus={b.booking_status} dueDate={b.due_date} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-left text-sm font-inter min-w-[720px]">
            <thead className="bg-white/5 text-white/45 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Ref</th>
                <th className="px-4 py-3 font-medium">Fee</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-white/10 hover:bg-white/[0.03]">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link to={`/admin/bookings/${b.id}`} className="hover:text-primary">
                      {formatDisplayDate(b.performance_date)}
                      <span className="block text-white/40 text-xs">
                        {formatTime(b.start_time)}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/bookings/${b.id}`} className="hover:text-primary">
                      {b.clients?.venue_or_client_name ?? "—"}
                      <span className="block text-white/40 text-xs">
                        {CATEGORY_LABELS[b.category]}
                      </span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-white/50">{b.reference}</td>
                  <td className="px-4 py-3">{formatGbpFromPence(b.fee_pence)}</td>
                  <td className="px-4 py-3">
                    <PaymentStatusBadge
                      paymentStatus={b.payment_status}
                      bookingStatus={b.booking_status}
                    dueDate={b.due_date}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </AdminShell>
  );
};

export default BookingsList;
